import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useExpensesOwner } from "../expenses/hooks/useExpensesOwner";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { MENU, PAYROLL_BASE } from "./config";
import { useMenuEntitlement } from "../../hooks/useEntitlement";
import { useStaffAccess } from "../../hooks/useIsStaffAdmin";
import { isTabVisibleToApprover, isTabVisibleToDev } from "./access";
import { useHoursApprover } from "../../hooks/useHoursApprover";

// Shell único de la mini-app "Asistencia, Anticipos y Nómina". Renderiza el sub-menú
// plano de secciones (derivado de MENU), el apps-sidebar y la sección activa vía
// <Outlet/>. Espeja TeamLayout / MantenimientoLayout.
export default function PayrollLayout() {
  // Ítems de menú por derecho (`menu:payroll.<id>`, #803).
  const canSeeMenuItem = useMenuEntitlement("payroll");
  const { t } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  // Las secciones con dinero no se le ofrecen a quien no administra (#742). El
  // control real está en el servidor; esto evita ofrecer una pantalla que
  // respondería 403.
  // #1007: todos ven las mismas secciones; cada endpoint devuelve solo lo suyo a
  // quien no administra. La única excepción es la nómina pagada contra estados
  // de cuenta, que es de los dueños (#1027).
  const isOwner = useExpensesOwner() === true;
  // #1142: quien no administra al equipo solo ve «Mis pagos» y «Asistencia»…
  // #1346: …y «Horas por aprobar», donde registra sus horas y sigue sus solicitudes.
  const { isStaffAdmin } = useStaffAccess(true, "payroll");
  // #1281: quien aprueba horas (tech lead / líder de squad) ve «Autorización de
  // incidencias» aunque no administre Nómina: ahí revisa las horas de su gente.
  const approver = useHoursApprover(!isStaffAdmin);
  // Encima, lo que el rol tenga desmarcado en su menú (#803).
  const visibleMenu = MENU.filter(
    (item) =>
      (isStaffAdmin || isTabVisibleToDev(item.id) || (approver.canApprove && isTabVisibleToApprover(item.id))) &&
      (!item.ownerOnly || isOwner) &&
      canSeeMenuItem(item.id),
  );

  const menuItems = visibleMenu.map((item) => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    path: `${PAYROLL_BASE}/${item.id}`,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="flex h-screen pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={t("payroll.appName")}
        subtitle={t("payroll.appSubtitle")}
        onOpenAppsSidebar={openAppsSidebar}
      />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname === `${PAYROLL_BASE}/${itemId}`}
          footerStats={{
            label: t("payroll.appName"),
            items: [{ label: t("payroll.menu.attendance"), value: menuItems.length }],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="payroll"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
